/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import TableLayout from './table/tableLayout';
import { Columns } from './table/constants';
import { DeliveryContext } from '../../contexts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { fetchDeliveries } from '../../hooks/fetch-hooks/get-all-deliveries/fetch-deliveries';
import { toast } from 'react-toastify';
import { request } from '../../config/helpers/axios-instance';
import { DELIVERY_PARTNERS, DELIVERY_TABS } from '../../components/delivery/constants';
import { useAddModalMain } from '../../contexts/address-modal/AddModal';
import { copyTrackingLink, generateLabel, getDeliveryTrackingLink, isManualDelivery } from '../../components/delivery/helper';
import BulkMarkDeliveredModal from './BulkMarkDeliveredModal';
import DeliveryTrackingModal from '../../components/delivery/DeliveryTrackingModal';


function Delivery() {
    const { AddopenModal, AddcloseModal } = useAddModalMain();

    const urlParams = new URLSearchParams(location.search);
    const page = parseInt(urlParams.get('page')) >= 1 ? parseInt(urlParams.get('page')) : 1;
    const searchStr = urlParams.get('search') || '';
    const pageSizeStr = parseInt(urlParams.get('size')) || 50;
    const tabParam = urlParams.get('tab');
    const initialTab = Object.values(DELIVERY_TABS).includes(tabParam) ? tabParam : DELIVERY_TABS.NEW;

    const [currentPage, setCurrentPage] = useState(page);
    const [search, setSearch] = useState(searchStr);
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);
    // eslint-disable-next-line no-unused-vars
    const [pageSize, setPageSize] = useState(pageSizeStr);

    const [generatingLabel, setGeneratingLabel] = useState('')
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [generatingLabels, setGeneratingLabels] = useState(false);

    const [cancelLoading, setCancelLoading] = useState(false);
    const [markDeliveredLoading, setMarkDeliveredLoading] = useState(false);
    const [markDeliveredBulkLoading, setMarkDeliveredBulkLoading] = useState(false);
    const [showBulkMarkModal, setShowBulkMarkModal] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);

    const { data: deliveriesData, refetch } = fetchDeliveries(currentPage, pageSize, search, activeTab);

    const cancelOrder = async (id) => {
        if (!id) {
            toast.warn("Invalid ID")
            return;
        }

        if (!window.confirm("Are you sure you want to cancel this order? This cannot be undone.")) {
            return;
        }

        setCancelLoading(true);

        try {
            const response = await request({
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                url: `delivery/cancel/${id}`,
            });

            if (response.data && response.data.success) {
                toast.success("Order Cancelled");
                refetch();
            } else {
                toast.error("Error cancelling order");
            }
        } catch (error) {
            toast.error("Error cancelling order");
        }

        setCancelLoading(false);
    }

    const markDelivered = async (id) => {
        if (!id) {
            toast.warn("Invalid ID");
            return;
        }

        setMarkDeliveredLoading(true);

        try {
            const response = await request({
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                url: `delivery/markDelivered/${id}`,
            });

            if (response.data && response.data.success) {
                toast.success("Marked as delivered");
                refetch();
            } else {
                toast.error(response.data?.err || "Error marking delivery as delivered");
            }
        } catch (error) {
            toast.error("Error marking delivery as delivered");
        }

        setMarkDeliveredLoading(false);
    };

    const markDeliveredBulk = async () => {
        if (selectedIds.size === 0) return;

        setMarkDeliveredBulkLoading(true);

        try {
            const response = await request({
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                url: 'delivery/markDeliveredBulk',
                data: { deliveries: Array.from(selectedIds).map(id => ({ id })) },
            });

            if (response.data && response.data.success) {
                const { succeeded, failed, total } = response.data.data.summary;
                if (failed === 0) {
                    toast.success(`${succeeded} of ${total} marked as delivered`);
                } else {
                    toast.warn(`${succeeded} marked as delivered, ${failed} failed`);
                }
                setSelectedIds(new Set());
                refetch();
            } else {
                toast.error(response.data?.err || "Error marking deliveries as delivered");
            }
        } catch (error) {
            toast.error("Error marking deliveries as delivered");
        }

        setMarkDeliveredBulkLoading(false);
    };

    const generateManifest = async (id) => {
        if (!id) {
            toast.warn("Invalid ID")
            return;
        }

        try {
            const response = await request({
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // `generateManifest`, not `generate-manifest` -- the hyphenated spelling matched no
                // route, so this silently 404'd. Corrected 2026-09-23.
                url: `delivery/generateManifest/${id}`,
            });

            if (response.data && response.data.success) {
                toast.success("Manifest Generated");
                window.open(response.data?.data?.manifestUrl, '_blank');
            } else {
                toast.error("Error generating manifest");
            }
        } catch (error) {
            toast.error("Error generating manifest");
        }
    }

    const handleGenerateLabel = async (deliveryId) => {
        setGeneratingLabel(deliveryId)
        await generateLabel(deliveryId)
        setGeneratingLabel('');
    }

    const handleDeliveryTracking = async (deliveryData) => {
        AddopenModal(() => <DeliveryTrackingModal deliveryData={deliveryData} onClose={AddcloseModal} />)
    }

    const toggleSelectId = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === tableData.length && tableData.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(tableData.map(d => d.id)));
        }
    };

    const generateBulkLabels = async () => {
        if (selectedIds.size === 0) {
            toast.warn("Please select at least one delivery");
            return;
        }

        setGeneratingLabels(true);
        try {
            const response = await request({
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                url: 'delivery/generateLabels',
                data: { deliveryIds: Array.from(selectedIds) },
            });

            if (response.data && response.data.success) {
                const data = response.data.data;
                const mergedLabelUrl =
                    typeof data === 'string'
                        ? data
                        : data?.labelUrl;

                if (!mergedLabelUrl) {
                    toast.error("No merged label URL returned");
                    return;
                }

                toast.success("Labels generated");
                window.open(mergedLabelUrl, '_blank');

                setSelectedIds(new Set());
            } else {
                toast.error("Error generating labels");
            }
        } catch (error) {
            toast.error("Error generating labels");
        } finally {
            setGeneratingLabels(false);
        }
    };

    const Cell = ({ cell: { row: { original } } }) => {
        const manual = isManualDelivery(original);
        const trackingLink = getDeliveryTrackingLink(original);
        const isShiprocket = parseInt(original?.deliveryPartnerId) === DELIVERY_PARTNERS.SHIPROCKET.partnerId;
        const isDtdc = parseInt(original?.deliveryPartnerId) === DELIVERY_PARTNERS.DTDC.partnerId;
        const isDelivered = original.status === 'DELIVERED';
        const isCancelled = original.status === 'CANCELLED';

        return (
        <div className="w-full py-2 h-full flex flex-col gap-1 justify-evenly" >
            {!isDelivered && !isCancelled && (
                <button
                    className="bg-red-500 flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold"
                    onClick={async () => await cancelOrder(original.id)}
                >
                    {cancelLoading ? "Cancelling" : "Cancel Order"}
                </button>
            )}
            {activeTab === DELIVERY_TABS.NEW && !isDelivered && !isCancelled && (
                <button
                    disabled={markDeliveredLoading}
                    className="bg-emerald-600 flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold disabled:opacity-60"
                    onClick={async () => await markDelivered(original.id)}
                >
                    {markDeliveredLoading ? "Updating..." : "Mark Delivered"}
                </button>
            )}
            {trackingLink && <button className='bg-[#2563eb] flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold' onClick={() => copyTrackingLink(trackingLink)} >Copy Tracking Link</button>}
            {isShiprocket && !manual && original.status !== 'CANCELLED' && !original?.courierDetails?.manifestUrl && <button className='bg-red-500 flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold' onClick={async () => await generateManifest(original.id)} >Generate Manifest</button>}
            {isShiprocket && !manual && original?.courierDetails?.manifestUrl && <a className='bg-yellow-300 flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold' href={original?.courierDetails?.manifestUrl} target={'_blank'} rel={'noreferrer'} >Download Manifest</a>}
            {!manual && <button disabled={original.status === 'CANCELLED'} className={`${original.status === 'CANCELLED' ? 'bg-gray-400' : 'bg-green-400'} flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold`} onClick={async () => await handleGenerateLabel(original?.id)} >{generatingLabel === original?.id ? "Generating..." : "Generate Label"}</button>}
            {isDtdc && <button disabled={original.status === 'CANCELLED'} className={`${original.status === 'CANCELLED' ? 'bg-gray-400' : 'bg-green-400'} flex justify-center items-center text-[#fff] p-[0.5rem] rounded-[5px] font-semibold`} onClick={async () => await handleDeliveryTracking(original)} >Track</button>}
        </div >
        );
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
        setSelectedIds(new Set());
    };

    useEffect(() => {
        const url = new URL(location);
        url.searchParams.set('page', currentPage);
        url.searchParams.set('size', pageSize);
        url.searchParams.set('search', search);
        url.searchParams.set('tab', activeTab);
        history.pushState({}, '', url);
    }, [currentPage, search, pageSize, activeTab, history, location]);

    useEffect(() => {
        if (deliveriesData?.data?.data) {
            setTableData(deliveriesData.data.data.rows);
            setTotal(deliveriesData.data.data.count);
            setSelectedIds(new Set());
        }
    }, [deliveriesData]);

    /* eslint-disable react/prop-types */
    const SelectHeader = () => (
        <input
            type="checkbox"
            checked={selectedIds.size === tableData.length && tableData.length > 0}
            onChange={toggleSelectAll}
            className="w-4 h-4 cursor-pointer accent-[#00A48B]"
        />
    );

    const SelectCell = ({ cell: { row: { original } } }) => (
        <input
            type="checkbox"
            checked={selectedIds.has(original.id)}
            onChange={() => toggleSelectId(original.id)}
            className="w-4 h-4 cursor-pointer accent-[#00A48B]"
        />
    );

    const columns = useMemo(() => [
        {
            id: 'selection',
            Header: SelectHeader,
            Cell: SelectCell,
        },
        ...Columns,
        {
            Header: 'Action',
            accessor: 'action',
            Cell,
        },
    ], [generatingLabel, selectedIds, tableData, activeTab, cancelLoading, markDeliveredLoading]);

    const deliveryTabs = [
        { id: DELIVERY_TABS.NEW, label: 'New' },
        { id: DELIVERY_TABS.ERROR, label: 'Error' },
        { id: DELIVERY_TABS.DELIVERED, label: 'Delivered' },
        { id: DELIVERY_TABS.CANCELLED, label: 'Cancelled' },
    ];

    return (
        <>
        <div className="p-7 h-full overflow-y-scroll bg-gray-50">
            <div className="flex gap-2 border-b border-gray-200 mb-4">
                {deliveryTabs.map(({ id, label }) => (
                    <button
                        key={id}
                        type="button"
                        onClick={() => handleTabChange(id)}
                        className={`px-4 py-2 font-sans text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === id
                            ? 'border-[#00A48B] text-[#00A48B]'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <div className="flex gap-2 items-center my-[0.5rem]">
                <button
                    onClick={() => {
                        if (currentPage !== 1) {
                            setCurrentPage(1)
                        } else {
                            refetch();
                        }
                    }}
                    className='flex gap-2 items-center justify-center bg-[#00A48B] py-[1rem] px-[1.5rem] rounded-lg'
                >
                    <FontAwesomeIcon icon={faRefresh} color='#fff' />
                    <span className='text-[1.2rem] font-medium text-[#fff]'>Refresh</span>
                </button>
                {selectedIds.size > 0 && (
                    <button
                        onClick={generateBulkLabels}
                        disabled={generatingLabels}
                        className='flex gap-2 items-center justify-center bg-[#00A48B] py-[1rem] px-[1.5rem] rounded-lg disabled:opacity-50'
                    >
                        <span className='text-[1.2rem] font-medium text-[#fff]'>
                            {generatingLabels ? 'Generating...' : `Generate Labels (${selectedIds.size})`}
                        </span>
                    </button>
                )}
                {selectedIds.size > 0 && activeTab === DELIVERY_TABS.NEW && (
                    <button
                        onClick={markDeliveredBulk}
                        disabled={markDeliveredBulkLoading}
                        className='flex gap-2 items-center justify-center bg-emerald-600 py-[1rem] px-[1.5rem] rounded-lg disabled:opacity-50'
                    >
                        <span className='text-[1.2rem] font-medium text-[#fff]'>
                            {markDeliveredBulkLoading ? 'Marking...' : `Mark Delivered (${selectedIds.size})`}
                        </span>
                    </button>
                )}
                <button
                    onClick={() => setShowBulkMarkModal(true)}
                    className='flex gap-2 items-center justify-center bg-emerald-600 py-[1rem] px-[1.5rem] rounded-lg'
                >
                    <span className='text-[1.2rem] font-medium text-[#fff]'>Bulk Mark Delivered</span>
                </button>
            </div>
            <div className="flex gap-4 items-center">
                <input
                    className="px-6 w-full font-sans bg-slate-100 py-4"
                    placeholder={`${total} records...`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            <DeliveryContext.Provider value={{ data: tableData, columns, refetch }}>
                {tableData.length > 0 ? <TableLayout /> : (
                    <div className="mt-6 rounded-lg bg-white p-8 text-center text-gray-500 font-sans">
                        No deliveries found
                    </div>
                )}
            </DeliveryContext.Provider>
            <div className="btn_div">
                <button disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)} className="previous">Previous</button>
                <span className="pageindex">Page {currentPage} of {Math.ceil(total / pageSize)}</span>
                <button disabled={currentPage >= Math.ceil(total / pageSize)} onClick={() => setCurrentPage(currentPage + 1)} className="next">Next</button>
            </div>
        </div>

        {showBulkMarkModal && (
            <BulkMarkDeliveredModal
                onClose={() => setShowBulkMarkModal(false)}
                onSuccess={refetch}
            />
        )}
        </>
    );
}

export default Delivery;
